<template>
  <div>
    <div v-if="allowSendingMessages">
      <div v-if="isFileUploading">
        <div class="mt-6" aria-hidden="true">
          <div class="overflow-hidden rounded-full bg-gray-200">
            <div
              class="h-2 rounded-full bg-indigo-600"
              :style="{ width: `${isFileUploading}%` }"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center h-16 bg-gray-100 px-4 shadow-md border-t">
        <button
          id="file-upload-attach"
          class="text-gray-500 hover:text-gray-700 mr-2 w-6 h-6"
          @click="triggerFileUpload"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
            />
          </svg>
        </button>

        <input
          type="file"
          ref="fileInput"
          class="hidden"
          @change="handleFileUpload"
        />

        <SavedRepliesDropdown @replySelected="insertReply" />

        <textarea
          type="text"
          ref="editor"
          placeholder="Type a message"
          v-model="body"
          class="flex-grow border-none rounded-full bg-white-100 px-4 py-2 text-sm focus:outline-none resize-none"
          @keyup.enter="sendMessage"
        />

        <button
          class="flex items-center justify-center bg-green-500 hover:bg-green-600 rounded-full text-white p-2 ml-2"
          @click="sendMessage"
        >
          <svg
            class="w-5 h-5 transform rotate-45"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            ></path>
          </svg>
        </button>
      </div>
    </div>
    <div v-else class="flex items-center justify-between bg-white px-4">
      <p class="text-sm text-center py-2 text-red-600 font-medium  px-2 my-2">
        ⚠️ You can't send message to this customer - Time limit exceeded
      </p>
      <span>
        <button
          v-if="allowTemplateMessage"
          class="mt-2 border border-green-500 text-green-500 hover:bg-green-50 font-bold py-2 px-4 rounded"
          @click="openMessageComposeModal"
        >
          Send Template
        </button>
        <button
          v-else
          class="mt-2 border border-green-500 text-green-500 hover:bg-green-50 font-bold py-2 px-4 rounded"
          @click="sendToCrisp"
        >
          Request Access to send template message
        </button>
      </span>
    </div>
    <MessageComposeModal
      v-if="isMessageComposeModalOpen"
      :phoneNumber="conversation.customer.phoneNumber"
      @close="closeMessageComposeModal"
      @send="handleSendTemplate"
    ></MessageComposeModal>
  </div>
</template>

<script>
import { differenceInHours } from "date-fns";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL
} from "firebase/storage";
import SavedRepliesDropdown from "../Common/SavedRepliesDropdown.vue";
import MessageComposeModal from "./MessageComposeModal.vue";

export default {
  name: "ChatEditor",
  components: {
    SavedRepliesDropdown,
    MessageComposeModal
  },
  props: {
    value: {
      type: String
    },
    conversation: {
      type: Object
    }
  },
  data() {
    return {
      body: "",
      fileUploadProgress: 0,
      isFileUploading: false,
      isMessageComposeModalOpen: false
    };
  },
  computed: {
    allowSendingMessages() {
      const diff = differenceInHours(
        new Date(),
        new Date(this.conversation.lastCustomerReplyTime * 1000)
      );
      return diff < 24;
    },
    allowTemplateMessage() {
      const config = this.$store.getters["Inbox/getInboxStarterConfig"];
      if (config && config.isEnabled) {
        return true;
      }
      return false;
    }
  },
  mounted() {
    this.setFocusOnEditor();
  },
  methods: {
    sendMessage(event) {
      if (event && event.shiftKey) {
        console.log(event);
        return;
      }
      this.$emit("enter", this.body);
      this.body = "";
    },
    setFocusOnEditor() {
      if (this.$refs.editor) {
        this.$refs.editor.focus();
      }
    },
    insertReply(replyText) {
      this.body = replyText;
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        console.log("File selected:", file);
        this.saveFileToServer(file);
      }
    },
    getFileType(file) {
      if (!(file instanceof File)) {
        throw new Error("The provided argument is not a File object.");
      }

      const mimeType = file.type;

      if (mimeType.startsWith("image/")) {
        return "image";
      } else if (mimeType.startsWith("video/")) {
        return "video";
      } else if (mimeType.startsWith("audio/")) {
        return "audio";
      } else {
        return "file";
      }
    },
    async saveFileToServer(file) {
      const store = this.$store.getters.getStore;
      const conversationId = this.conversation.id;
      if (file.size > 5242880) {
        console.log("file size is greater than 5 mb can't upload ");
        return;
      }
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `${store.id}/${conversationId}/${Date.now()}_${file.name}`
      );
      const uploadTask = uploadBytesResumable(storageRef, file);

      this.isFileUploading = true;

      uploadTask.on(
        "state_changed",
        snapshot => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          this.fileUploadProgress = progress;
          console.log(`Upload is ${progress}% done`);
        },
        error => {
          console.error("File upload failed", error);
          this.isFileUploading = false;
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
            console.log("File available at", downloadURL);
            this.isFileUploading = false;
            this.fileUploadProgress = 0;
            this.$emit("sendAttachment", {
              name: file.name,
              type: this.getFileType(file),
              downloadURL
            });
          });
        }
      );
    },
    openMessageComposeModal() {
      this.isMessageComposeModalOpen = true;
    },
    closeMessageComposeModal() {
      this.isMessageComposeModalOpen = false;
    },
    handleSendTemplate({ phoneNumber, messageBody }) {
      console.log(
        "Template sent to:",
        phoneNumber,
        "with message:",
        messageBody
      );
      this.$store.dispatch("Inbox/sendTemplateMessage", {
        requestParams: {
          message: messageBody,
          phoneNumber: phoneNumber
        }
      });
      this.closeMessageComposeModal();
    },
    sendToCrisp() {
      // $crisp.push(["do", "chat:open"]); // eslint-disable-line
      // eslint-disable-next-line
      $crisp.push([
        "do",
        "message:send",
        ["text", `Need access to send template message in Team Inbox`]
      ]);
    }
  }
};
</script>

<style lang="scss" scoped>
/* Add any additional styles here if needed */
</style>
